import { Link } from "react-router-dom";
import { Flame, Leaf, Gift, Clock, Sparkles, Heart } from "lucide-react";
import { CATALOG, CANDLE_NAMES, IMG, fmt } from "@/data/catalog";
import { Reveal, SectionHeading, Overline, Stars, AddToCartButton, Btn } from "@/components/common/ui";

const WHY = [
  [Clock, "50+ Hour Burn", "Premium soy-wax blend for a long, clean, even burn."],
  [Leaf, "Clean Soy Wax", "Hand-poured on the Gold Coast, no nasties, just glow."],
  [Gift, "The Gift That Wows", "Realistic café-drink design everyone asks about."],
  [Heart, "Mood Booster", "An instant feel-good moment in every room."],
];

const CANDLE_REVIEWS = [
  ["@bree", "The Iced Matcha Latte one is my new favourite, it makes the whole room feel so fresh.", "Matcha Latte"],
  ["@jessathome", "I light mine every morning before work. It genuinely relaxes me and makes the house feel cosy.", "Chai Latte"],
  ["@samanthacreates", "I bought one for myself and ended up ordering three more as gifts. Everyone asks where I got them.", "Cold Brew Coffee"],
];

export default function Candles() {
  return (
    <div data-testid="candles-page" className="bg-[#FFFCFA]">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-6 py-14 md:grid-cols-2 md:px-12 md:py-20">
          <Reveal>
            <Overline>New · Aura Glow Home</Overline>
            <h1 className="mt-5 font-display text-[3rem] font-light leading-[1] tracking-tight text-[#2C2B2C] sm:text-7xl">
              Café Luxe<br /><span className="italic text-[#FBC4A8]">Candle Collection.</span>
            </h1>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#8A7F86] md:text-lg">
              Candles that look and smell exactly like your favourite café drinks. Hand-poured with a premium soy-wax blend on the Gold Coast, cosy, creamy and impossibly cute.
            </p>
            <div className="mt-7 flex items-center gap-3">
              <Stars n={5} /><span className="text-sm text-[#8A7F86]">4.9 · #1 Best-Seller</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#scents" className="rounded-full bg-[#232222] px-8 py-4 text-xs uppercase tracking-[0.14em] text-[#FFFCFA] transition-all hover:-translate-y-0.5" data-testid="candles-hero-cta">Shop the Scents</a>
              <Btn to="/product/leopard-luxe-led-light-therapy-mask" variant="outline" testid="candles-hero-mask">Discover the Mask</Btn>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="hover-zoom overflow-hidden rounded-[2rem] border border-[#F2E8E6] soft-shadow">
            <img src={IMG.candleHero} alt="Aroma Glow café candle collection" className="aspect-[4/3] w-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* Why special */}
      <section className="border-y border-[#F2E8E6] bg-white">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px bg-[#F2E8E6] md:grid-cols-4">
          {WHY.map(([Icon, t, d]) => (
            <div key={t} className="flex flex-col items-center gap-2 bg-white px-4 py-9 text-center">
              <Icon className="h-6 w-6 text-[#FBC4A8]" strokeWidth={1.5} />
              <p className="font-display text-lg text-[#2C2B2C]">{t}</p>
              <p className="text-xs text-[#8A7F86]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Scent grid */}
      <section id="scents" className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <SectionHeading align="center" overline="6 Signature Scents" title="Pick your morning ritual."
            sub="Each candle smells exactly like your favourite café order. Which one's you?" className="mb-16" />
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {CANDLE_NAMES.map((sku, i) => {
              const c = CATALOG[sku];
              return (
                <Reveal key={sku} delay={(i % 3) * 0.08} className="group flex flex-col overflow-hidden rounded-[2rem] border border-[#F2E8E6] bg-white">
                  <div className="hover-zoom overflow-hidden">
                    <img src={c.image} alt={c.name} className="aspect-square w-full object-cover" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-2xl text-[#2C2B2C]">{c.emoji} {c.name}</h3>
                    <p className="mt-1.5 text-sm font-light text-[#8A7F86]">{c.subtitle}</p>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="font-display text-2xl text-[#2C2B2C]">{fmt(c.price)}</span>
                      <span className="text-sm text-[#8A7F86] line-through">{fmt(c.compareAt)}</span>
                    </div>
                    <div className="mt-5"><AddToCartButton sku={sku} label="Add to Bag" variant="outline" className="w-full" testid={`candle-add-${sku}`} /></div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lifestyle */}
      <section className="py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-6 md:grid-cols-2 md:px-12">
          <Reveal className="hover-zoom overflow-hidden rounded-[2rem] border border-[#F2E8E6] soft-shadow">
            <img src={IMG.candleLife} alt="Cosy candle moment" className="aspect-[4/5] w-full object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>More Than a Candle</Overline>
            <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[#2C2B2C] sm:text-5xl">A mood, a memory, a moment to slow down.</h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#8A7F86]">
              Aroma Glow isn't just a candle. It's a mood booster, a conversation starter and a reminder to savour the little things. Pair it with your evening LED ritual for the ultimate wind-down.
            </p>
            <ul className="mt-7 space-y-3">
              {["Boosts your mood instantly", "Instagram-worthy realistic design", "100+ hour premium soy-jelly blend", "7-Day money-back guarantee"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#2C2B2C]"><Flame className="h-4 w-4 text-[#FBC4A8]" /> {t}</li>
              ))}
            </ul>
            <div className="mt-8"><Btn to="/product/leopard-luxe-led-light-therapy-mask" variant="primary" testid="candles-pair-mask">Pair With the Mask</Btn></div>
          </Reveal>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <SectionHeading align="center" overline="See What Others Are Saying" title="Loved across Australia." className="mb-14" />
          <div className="grid gap-6 md:grid-cols-3">
            {CANDLE_REVIEWS.map(([handle, text, scent], i) => (
              <Reveal key={handle} delay={i * 0.08} className="rounded-[2rem] border border-[#F2E8E6] bg-white p-7">
                <Stars n={5} />
                <p className="mt-4 text-[0.95rem] font-light leading-relaxed text-[#2C2B2C]">"{text}"</p>
                <p className="mt-5 text-sm font-medium text-[#2C2B2C]">{handle} <span className="font-normal text-[#8A7F86]">· {scent}</span></p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-sell the mask */}
      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid items-center gap-10 overflow-hidden rounded-[2rem] border border-[#F2E8E6] bg-[#FFF0E8] md:grid-cols-2">
            <div className="p-8 md:p-14">
              <Overline>The Main Event</Overline>
              <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[#2C2B2C] sm:text-5xl">Don't forget the glow that started it all.</h2>
              <p className="mt-5 max-w-md text-base font-light text-[#8A7F86]">Our award-winning Leopard Luxe™ LED Light Therapy Mask, salon-grade skin therapy in 10 minutes a day. Light a candle, slip it on, and unwind.</p>
              <div className="mt-7 flex flex-wrap gap-4">
                <Btn to="/product/leopard-luxe-led-light-therapy-mask" variant="primary" testid="candles-shop-mask">Shop the Mask</Btn>
                <span className="flex items-center gap-2 text-sm text-[#8A7F86]"><Sparkles className="h-4 w-4 text-[#FBC4A8]" /> 1,000+ five-star reviews</span>
              </div>
            </div>
            <div className="hover-zoom overflow-hidden md:h-[30rem]">
              <img src={IMG.product} alt="Leopard Luxe™ LED Light Therapy Mask" className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
